import * as React from "react";


import SearchDropdown from "@cayuse/shared-react.components.search-dropdown";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { Model, _ } from "@flowable/forms";

import FlowableErrorComponent from "../ErrorComponent/FlowableErrorComponent";
import styles from "./styles.css";
import "./flowableFinder.min.css";

const checkDefinition = {
	type: "select",
	value: "{{selectVal}}",
};

const getDefaultRenderLabel = ({ tooltip, text }) => ({
	content: <span title={tooltip}>{text}</span>,
});

class FlowableFinder extends Model.StatefulFormComponent {
	constructor (props) {
		super(props);

		this.state = { showErrors: false };
	}

	shouldComponentUpdate (nextProps, nextState) {
		return (
			nextProps.preselectedOptions !== this.props.preselectedOptions ||
            nextState !== this.state
		);
	}

	static $resolve (unresolved, scope, addData, Components) {
		const resolve = Components.panel.$resolve;
		const resolved = resolve(unresolved, scope, addData);
		const disabled = resolved.enabled === false;
		const resolvedCheck = resolve(
			checkDefinition,
			resolved.value,
			addData,
			disabled
		);

		return { ...resolved, resolvedCheck };
	}

    onFocus = () => {
    	this.dispatchFocusEvent();
    };

    render () {
    	const { config, Components, preselectedOptions, search } =
            this.props;

    	const disabled = this.props.config.enabled === false;
    	const bem = this.bem("panel", { disabled });
    	const placeholder = this.getFromConfig("placeholder");

    	let value = config.value !== "" ? config.value : undefined;

    	if (config.extraSettings.multi) {
    		const multiValue = Array.isArray(value) ? value : [ value ];
    		value = value === undefined || value === null ? [] : multiValue;
    	} else {
    		value = Array.isArray(value) ? value[0] : value;
    	}

    	const empty =
            config.isRequired &&
            (!value || (config.extraSettings.multi && !isNonEmptyArray(value)));
    	const maxItems = config.extraSettings.multi ? config.extraSettings.maxItems : undefined;
    	const minItems = config.extraSettings.multi ? config.extraSettings.minItems : undefined;
    	const minError = minItems && value?.length < minItems;
    	const maxError = maxItems && value?.length > maxItems;
    	const isValid = !empty && !minError && !maxError;

    	// Add active class on focus and remove on blur because there is no focus indication on keyboard navigation
    	const toggleClassActiveOnFocus = (element, action) => {
    		if (action === "add") {
    			if (!element.classList.contains("active")) {
    				element.classList.add("active");
    			}
    		} else {
    			if (element.classList.contains("active")) {
    				element.classList.remove("active");
    			}
    		}
    	};

    	return (
    		<div
    			data-flw-id={config.id}
    			className={`${bem()} ${styles.flowableSearch} ${
                    isValid ? "" : "empty"
                }`}
    		>
    			{!this.props.hideLabel && (
    				<Components.label
    					{...this.props}
    					config={config}
    					className={`${
                            config.isRequired
                            	? "flw__component--required__label"
                            	: ""
                        }`}
    				/>
    			)}
    			<SearchDropdown
    				value={value}
    				disabled={!config.enabled}
    				search={options => options}
    				selection
    				selectOnNavigation={false}
    				forceSelection={false}
    				selectOnBlur={false}
    				onGetDataPromise={search}
    				searchingMessage="Loading..."
    				placeholder={placeholder}
    				onChange={(value, selectedOptions) => {
						this.setState({ showErrors: false });
    					this.props.searchChange(selectedOptions);
					}}
    				onBlur={e => {
    					toggleClassActiveOnFocus(e.target.parentElement, "remove");
						this.setState({ showErrors: true });
    				}}
    				onFocus={e => {
    					toggleClassActiveOnFocus(e.target.parentElement, "add");
    					this.onFocus();
    				}}
    				isRequired={config.isRequired}
    				renderLabel={getDefaultRenderLabel}
    				prefetchedOptions={preselectedOptions}
    				multiple={config.extraSettings.multi}
    				narrowCriteriaHint={{ show: true }}
    				loadingIcon={
    					<span className="loading-indicator" aria-hidden="true">
    						<span className="dot-flashing" />
    					</span>
    				}
    				fieldLabel={`${config?.label} ${config?.description ? config?.description : ""}`}
    				icon={
    					<span className="caret-icon" aria-hidden="true">
    						<svg
    							height="20"
    							width="20"
    							viewBox="0 0 20 20"
    							aria-hidden="true"
    							focusable="false"
    						>
    							<path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
    						</svg>
    					</span>
    				}
    				clearIcon={
    					<button type="button" aria-label="Clear" className={styles.clearButton} disabled={disabled}>
    						<span className="clear-icon" style={{ pointerEvents: disabled ? "none" : undefined }}>
    							<svg
    								height="20"
    								width="20"
    								viewBox="0 0 20 20"
    							>
    								<path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
    							</svg>
    						</span>
    					</button>
    				}
    			/>
    			<Components.description
    				{...this.props}
    				className={bem("description")}
    			/>

    			<div role="alert">
    				{!disabled && this.state.showErrors && (
    					<FlowableErrorComponent
    						extraSettings={config.extraSettings}
    						customValidations={config.customValidations}
    						empty={empty}
    						minError={minError}
    						maxError={maxError}
    					/>
    				)}
    			</div>
    		</div>
    	);
    }
}

export default FlowableFinder;
